import { Icon, ModalButton } from "adviesbox-shared";
import { LabelValuePairs } from "adviesbox-shared/utils/types";
import classNames from "classnames";
import { Field, getIn, useFormikContext } from "formik";
import React, { ReactElement, useCallback, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableProvidedDragHandleProps,
  DraggableStateSnapshot,
  Droppable,
  DropResult
} from "react-beautiful-dnd";
import { Button, Collapse } from "react-bootstrap";
import { RapportageSamenstellenState, RapportageElementType } from "./infra/rapportage-samenstellen-types";
import { RapportageElementSoort } from "./infra/rapportage-structuur-types";
import classes from "./rapportage-structuur.module.scss";
import { TitelAanpassenModal } from "./titel-aanpassen-modal";

export type RapportageStructuurSamenstellenProps = {
  element: RapportageElementType;
  parentKey: number;
  parent: string;
  dragableProps?: any;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  isDragging?: boolean;
  setForceCloseByParrent?: any;
  forceCloseByParrent?: boolean;
  adoptionList?: LabelValuePairs;
};

export const RapportageStructuurElementen = ({
  element,
  parentKey,
  parent,
  dragableProps,
  dragHandleProps,
  isDragging,
  adoptionList
}: RapportageStructuurSamenstellenProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const { values, setFieldValue } = useFormikContext<RapportageSamenstellenState>();

  /* istanbul ignore next reason: Unable to test drag and drop functionality using @testing-library/react. An update for this should be on the roadmap of rtl */
  const handleOnDragEnd = useCallback(
    (result: DropResult): void => {
      const elementName = result.draggableId;
      const destructedElementName = elementName.split(".");

      destructedElementName.pop();

      const parentName = destructedElementName.join(".");
      const parentState = getIn(values, parentName);

      const elementen = parentState.elementen;

      const [reorderedItem] = elementen.splice(result.source.index, 1);
      elementen.splice(result.destination?.index, 0, reorderedItem);

      setFieldValue(`${parentName}.elementen`, elementen);
    },
    [values, setFieldValue]
  );

  const dragAndDropIconPrefab = (): ReactElement => {
    return (
      <span className={classes.drag_n_drop_wrapper} {...dragHandleProps}>
        {parentKey > parentKey + 1 && <Icon name={"drag"} />}
      </span>
    );
  };

  const spacersPrefab = (): ReactElement[] => {
    return [...new Array(parentKey)].map((_v: undefined, index: number) => {
      return <div key={`${parent}[${index}]`} className={classes.spacer}></div>;
    });
  };

  /* istanbul ignore next */
  const updateChildrenRecursively = useCallback(
    (parentPath: string, newValue: boolean): void => {
      const currentElement = getIn(values, parentPath);
      if (!currentElement || !currentElement.elementen) return;

      currentElement.elementen.forEach((child: RapportageElementType, index: number) => {
        const childPath = `${parentPath}.elementen[${index}]`;

        // Update de geselecteerd waarde van het kind element
        setFieldValue(`${childPath}.geselecteerd`, newValue);

        // Recursief bijwerken van geneste kinderen
        if (child.elementen && child.elementen.length > 0) {
          updateChildrenRecursively(childPath, newValue);
        }
      });
    },
    [values, setFieldValue]
  );

  /* istanbul ignore next */
  const updateParentRecursively = useCallback(
    (currentPath: string): void => {
      const pathArray = currentPath.split(".");
      pathArray.pop();
      const path = pathArray.join(".");

      if (!path) return;

      const parentElement = getIn(values, path);
      if (!parentElement || !("geselecteerd" in parentElement)) return;

      // Update de geselecteerd waarde van het parent element
      setFieldValue(`${path}.geselecteerd`, true);

      // Recursief bijwerken van hogere niveau ouders
      updateParentRecursively(path);
    },
    [values, setFieldValue]
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;

      // Update de geselecteerd waarde van het huidige element
      setFieldValue(`${parent}.geselecteerd`, newValue);

      // Update alle kinderen recursief
      updateChildrenRecursively(parent, newValue);

      // Update parent als dit element nu geselecteerd is
      if (newValue) {
        updateParentRecursively(parent);
      }
    },
    [parent, setFieldValue, updateChildrenRecursively, updateParentRecursively]
  );

  const checkboxPrefab = (): ReactElement => {
    // Controleer of dit een child element is (parentKey > 0) en of de directe parent geselecteerd is
    const isChildElement = parentKey > 0;
    let isParentSelected = true;

    if (isChildElement) {
      // Haal het parent pad op door het laatste deel van het huidige pad te verwijderen
      /* eslint-disable-next-line react/prop-types */
      const pathArray = parent.split(".");
      pathArray.pop();
      const parentPath = pathArray.join(".");
      isParentSelected = getIn(values, `${parentPath}.geselecteerd`);
    }

    return (
      <span
        className={classNames(classes.checkbox_wrapper, {
          [classes.checkbox_disabled]: isChildElement && !isParentSelected
        })}
      >
        <Field onChange={handleCheckboxChange} name={`${parent}.geselecteerd`} type={"checkbox"} />
      </span>
    );
  };

  const expandButtonPrefab = (): ReactElement => {
    return (
      <span className={classes.expand_wrapper}>
        {/* eslint-disable-next-line react/prop-types */}
        {element.elementen && element.elementen.length > 0 && (
          <Button
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-controls={`table_collapse_${parentKey}`}
            variant={"light"}
            className={classNames(classes.expand_btn, "font-weight-bold")}
            data-testid={`${parent}-expand-btn`}
          >
            {open ? "-" : "+"}
          </Button>
        )}
      </span>
    );
  };

  const editButtonPrefab = (): ReactElement | boolean => {
    return (
      !isDragging &&
      parentKey > 0 && (
        <div className={classes.edit_cell}>
          <ModalButton
            parent={`${parent}`}
            aria-label="Titel aanpassen"
            size="lg"
            content={<Icon name={"pencil"} />}
            data-testid={`${parent}.naam-aanpassen-btn`}
          >
            <TitelAanpassenModal
              parent={parent}
              onSave={(data: any) => {
                setFieldValue("structuren", data.structuren);
              }}
              adoptionList={adoptionList}
            />
          </ModalButton>
        </div>
      )
    );
  };

  let elementName = element.naam;
  switch (element.soortElement) {
    case RapportageElementSoort.Tekstblok:
      elementName = `Tekstblok: ${elementName}`;
      break;
    case RapportageElementSoort.Tabel:
      elementName = `Tabel: ${elementName}`;
      break;
    case RapportageElementSoort.Grafiek:
      elementName = `Grafiek: ${elementName}`;
      break;
    case RapportageElementSoort.Iteratie:
      elementName = `Iteratie: ${elementName}`;
      break;
  }

  return (
    <>
      <div key={parent} style={{ position: "relative" }} {...dragableProps}>
        <div className={classes.main_cell}>
          {dragAndDropIconPrefab()}

          {spacersPrefab()}

          {expandButtonPrefab()}

          {checkboxPrefab()}

          <span className={classNames(classes.label_wrapper, "name-span")} data-testid={`${parent}.naam`}>
            {elementName}
          </span>
        </div>

        {element.elementen && element.elementen.length > 0 && (
          <Collapse in={open} key={`collapse_${parentKey}`}>
            <div id={`table_collapse_${parentKey}`}>
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId={parent} type={parent} mode={"standard"}>
                  {providedParrent => (
                    <div
                      {...providedParrent.droppableProps}
                      ref={providedParrent.innerRef}
                      onDragCapture={e => e.stopPropagation()}
                    >
                      {element.elementen.map((v: RapportageElementType, i: number) => {
                        return (
                          <Draggable
                            key={`${parent}.elementen[${i}]`}
                            draggableId={`${parent}.elementen[${i}]`}
                            index={i}
                          >
                            {(providedChild, snapshot) => {
                              const dragHandleProps = getDragHandleProp(providedChild, snapshot, parent, i);

                              return (
                                <div style={{ position: "relative" }}>
                                  <RapportageStructuurElementen
                                    element={v}
                                    key={`${parent}.elementen[${i}]`}
                                    parentKey={parentKey + 1}
                                    parent={`${parent}.elementen[${i}]`}
                                    dragableProps={dragHandleProps}
                                    dragHandleProps={providedChild.dragHandleProps}
                                    isDragging={snapshot.isDragging}
                                    adoptionList={adoptionList}
                                  />
                                </div>
                              );
                            }}
                          </Draggable>
                        );
                      })}
                      {providedParrent.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </Collapse>
        )}

        {element.soortElement !== RapportageElementSoort.Iteratie && editButtonPrefab()}
      </div>
    </>
  );
};

function getDragHandleProp(
  providedChild: DraggableProvided,
  snapshot: DraggableStateSnapshot,
  parent: string,
  i: number
): any {
  const style = {
    ...providedChild.draggableProps.style,
    borderTop: "1px solid var(--darkest-grey)",
    borderRight: "1px solid var(--darkest-grey)",
    backgroundColor: "white",
    paddingTop: "-10px",
    paddingBottom: "10px"
  };

  return {
    ref: providedChild.innerRef,
    ...providedChild.draggableProps,
    style: snapshot.isDragging ? style : providedChild.draggableProps.style,
    "data-testid": `${parent}.elementen[${i}].handle`
  };
}
